/**
 * StatusWorkflow.tsx
 * 
 * Componente per la gestione del workflow di stati delle richieste nell'applicazione Ufficio Stile.
 * Gestisce la transizione tra stati automatici e manuali con validazioni e workflow rules.
 * Include visualizzazione progress, azioni disponibili e sincronizzazione con task associati.
 */

import React, { useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  Card,
  Badge,
  Box,
  Progress,
  Alert,
  Modal,
  Textarea,
  Stepper,
  ActionIcon,
  Menu,
  Tooltip
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconChevronRight,
  IconCheck,
  IconClock,
  IconPlayerPlay,
  IconPlayerPause,
  IconSquareCheck,
  IconX,
  IconRefresh,
  IconDots,
  IconAlertCircle,
  IconInfoCircle,
  IconSend,
  IconCircleCheck,
  IconCalendar
} from '@tabler/icons-react';
import { useRequests } from '../../../hooks';
import { Request, RequestStatus, RequestType, RequestUpdateRequest, TaskStatus, UserRole } from '../../../types/api';
import { CrossPortalCompletion } from '../../../components/cross-portal/CrossPortalCompletion';
import { toDateOnlyISOString } from '../../../utils/date-utils';
import { useOktaAuth } from '../../../providers/okta-auth-provider';

interface StatusWorkflowProps {
  request: Request;
  readOnly?: boolean;
  onStatusChange?: (newStatus: RequestStatus) => void;
}

interface StatusTransition {
  from: RequestStatus;
  to: RequestStatus;
  label: string;
  description: string;
  requiresConfirmation: boolean;
  requiresNote?: boolean;
  optionalNote?: boolean;
  color: string;
  icon: React.ReactNode;
  isAutomatic?: boolean;
  /** Se presente, la transizione è visibile solo a chi ha uno di questi ruoli (Campionatura). */
  allowedRoles?: UserRole[];
}

// Ruoli abilitati alle transizioni di Pre-Campionatura, allineati a RequestWorkflowPolicy (BE).
const UFFICIO_STILE_ROLES: UserRole[] = [UserRole.Admin, UserRole.CapoUfficioStile, UserRole.UfficioStile];
const INDUSTRIALIZZAZIONE_ROLES: UserRole[] = [UserRole.Admin, UserRole.CapoIndustrializzazione, UserRole.Industrializzazione];

export function StatusWorkflow({ request, readOnly = false, onStatusChange }: StatusWorkflowProps) {
  const [transitionModalOpen, setTransitionModalOpen] = useState(false);
  const [selectedTransition, setSelectedTransition] = useState<StatusTransition | null>(null);
  const [syncingStatus, setSyncingStatus] = useState(false);
  const [crossPortalModalOpen, setCrossPortalModalOpen] = useState(false);

  const { updateMutation, updateStatusMutation } = useRequests();
  const { hasRole } = useOktaAuth();

  // Check if this is a Bozzetti request - use simplified workflow
  // Handle both numeric enum (1) and string ("Bozzetto") from backend serialization
  const isBozzettiRequest = request.requestType === RequestType.Bozzetto ||
    request.requestType === 'Bozzetto' as unknown as RequestType;

  // Per i Gamma lo stato "Sospeso" è una chiusura definitiva (non riapribile)
  const isGammaRequest = request.requestType === RequestType.Gamma ||
    request.requestType === 'Gamma' as unknown as RequestType;

  // La Campionatura ha una fase preliminare (Pre-Campionatura) gestita da Ufficio Stile,
  // prima del passaggio al team Industrializzazione.
  const isCampionaturaRequest = request.requestType === RequestType.Campionatura ||
    request.requestType === 'Campionatura' as unknown as RequestType;

  // Una richiesta di Campionatura ha attraversato (o sta attraversando) la fase di
  // Pre-Campionatura se è in uno di quegli stati oppure se la data è già stata valorizzata
  // in passato (resta impostata anche dopo l'avvio della lavorazione). Le richieste create
  // prima dell'introduzione di questa fase (legacy) non la valorizzano mai.
  const hasPreCampionaturaPhase = isCampionaturaRequest && (
    request.status === RequestStatus.PreCampionatura ||
    request.status === RequestStatus.PreCampionaturaCompleted ||
    !!request.preCampionaturaCompletedAt
  );

  // Il completamento ha sempre una data di chiusura; per i Gamma anche la sospensione
  // (chiusura definitiva); per la Campionatura anche il completamento della Pre-Campionatura.
  const transitionNeedsClosureDate = (transition: StatusTransition): boolean =>
    transition.to === RequestStatus.Completed ||
    transition.to === RequestStatus.PreCampionaturaCompleted ||
    (isGammaRequest && transition.to === RequestStatus.Suspended);

  // Placeholder per sync mutation (da implementare nel hook)
  const syncRequestStatusMutation = {
    mutateAsync: async (requestId: string) => {
      // Placeholder implementation
      return Promise.resolve();
    }
  };

  // Form per conferma transizione
  const transitionForm = useForm({
    initialValues: {
      note: '',
      closedDate: new Date() as Date | null
    },
    validate: {
      note: (value, values) => {
        if (selectedTransition?.requiresNote && (!value || value.trim().length < 5)) {
          return 'Una nota è richiesta per questa transizione (minimo 5 caratteri)';
        }
        return null;
      }
    }
  });

  // Definizione workflow stati
  const statusWorkflow: StatusTransition[] = [
    {
      from: RequestStatus.New,
      to: RequestStatus.InProgress,
      label: 'Avvia Lavorazione',
      description: 'Inizia la lavorazione della richiesta',
      requiresConfirmation: false,
      color: 'blue',
      icon: <IconPlayerPlay size={16} />
    },
    {
      from: RequestStatus.InProgress,
      to: RequestStatus.Completed,
      label: 'Completa e Notifica',
      description: 'Completa la richiesta e notifica gli altri portali',
      requiresConfirmation: true,
      requiresNote: true,
      color: 'green',
      icon: <IconSend size={16} />
    },
    {
      from: RequestStatus.InProgress,
      to: RequestStatus.Suspended,
      label: 'Sospendi',
      description: 'Sospende la richiesta. Potrà essere ripresa in lavorazione in seguito.',
      requiresConfirmation: true,
      requiresNote: false,
      optionalNote: true,
      color: 'gray',
      icon: <IconPlayerPause size={16} />
    },
    {
      from: RequestStatus.Suspended,
      to: RequestStatus.InProgress,
      label: 'Riprendi Lavorazione',
      description: 'Riprende la lavorazione della richiesta sospesa',
      requiresConfirmation: false,
      color: 'blue',
      icon: <IconPlayerPlay size={16} />
    },
    {
      from: RequestStatus.Suspended,
      to: RequestStatus.Cancelled,
      label: 'Annulla',
      description: 'Annulla definitivamente la richiesta',
      requiresConfirmation: true,
      requiresNote: true,
      color: 'red',
      icon: <IconX size={16} />
    },
    {
      from: RequestStatus.Completed,
      to: RequestStatus.InProgress,
      label: 'Riapri',
      description: 'Riapre una richiesta completata per ulteriori modifiche',
      requiresConfirmation: true,
      requiresNote: true,
      color: 'yellow',
      icon: <IconRefresh size={16} />
    },
    {
      from: RequestStatus.New,
      to: RequestStatus.Cancelled,
      label: 'Annulla',
      description: 'Annulla definitivamente la richiesta',
      requiresConfirmation: true,
      requiresNote: true,
      color: 'red',
      icon: <IconX size={16} />
    },
    {
      from: RequestStatus.InProgress,
      to: RequestStatus.Cancelled,
      label: 'Annulla',
      description: 'Annulla definitivamente la richiesta',
      requiresConfirmation: true,
      requiresNote: true,
      color: 'red',
      icon: <IconX size={16} />
    }
  ];

  // Workflow semplificato per richieste Bozzetti: solo "In Attesa" (New) e "Completata"
  const bozzettiStatusWorkflow: StatusTransition[] = [
    {
      from: RequestStatus.New,
      to: RequestStatus.Completed,
      label: 'Completa Richiesta',
      description: 'Segna la richiesta bozzetto come completata',
      requiresConfirmation: true,
      requiresNote: false,
      color: 'green',
      icon: <IconCircleCheck size={16} />
    },
    {
      from: RequestStatus.Completed,
      to: RequestStatus.New,
      label: 'Riapri',
      description: 'Riapre una richiesta completata per ulteriori modifiche',
      requiresConfirmation: true,
      requiresNote: true,
      color: 'yellow',
      icon: <IconRefresh size={16} />
    },
    {
      from: RequestStatus.New,
      to: RequestStatus.Suspended,
      label: 'Sospendi',
      description: 'Sospende la richiesta. Potrà essere ripresa in seguito.',
      requiresConfirmation: true,
      requiresNote: false,
      optionalNote: true,
      color: 'gray',
      icon: <IconPlayerPause size={16} />
    },
    {
      from: RequestStatus.Suspended,
      to: RequestStatus.New,
      label: 'Riprendi Lavorazione',
      description: 'Riprende la richiesta sospesa',
      requiresConfirmation: false,
      color: 'blue',
      icon: <IconPlayerPlay size={16} />
    },
    {
      from: RequestStatus.Suspended,
      to: RequestStatus.Cancelled,
      label: 'Annulla',
      description: 'Annulla definitivamente la richiesta',
      requiresConfirmation: true,
      requiresNote: true,
      color: 'red',
      icon: <IconX size={16} />
    },
    {
      from: RequestStatus.New,
      to: RequestStatus.Cancelled,
      label: 'Annulla',
      description: 'Annulla definitivamente la richiesta',
      requiresConfirmation: true,
      requiresNote: true,
      color: 'red',
      icon: <IconX size={16} />
    }
  ];

  // Workflow per le richieste di Campionatura: fase di Pre-Campionatura (Ufficio Stile) seguita
  // dal flusso standard (Industrializzazione). Le richieste legacy, che nascono già in "Nuova"
  // e non hanno mai attraversato la Pre-Campionatura, usano semplicemente il flusso standard.
  const preCampionaturaStatusWorkflow: StatusTransition[] = [
    {
      from: RequestStatus.PreCampionatura,
      to: RequestStatus.PreCampionaturaCompleted,
      label: 'Completa Pre-Campionatura',
      description: 'Completa la fase di Pre-Campionatura: la richiesta passa in attesa di essere presa in carico dal team Industrializzazione.',
      requiresConfirmation: true,
      requiresNote: false,
      optionalNote: true,
      color: 'indigo',
      icon: <IconCircleCheck size={16} />,
      allowedRoles: UFFICIO_STILE_ROLES
    },
    {
      from: RequestStatus.PreCampionatura,
      to: RequestStatus.Cancelled,
      label: 'Annulla',
      description: 'Annulla definitivamente la richiesta',
      requiresConfirmation: true,
      requiresNote: true,
      color: 'red',
      icon: <IconX size={16} />,
      allowedRoles: UFFICIO_STILE_ROLES
    },
    {
      from: RequestStatus.PreCampionaturaCompleted,
      to: RequestStatus.InProgress,
      label: 'Avvia Lavorazione',
      description: 'Prende in carico la richiesta e avvia la lavorazione della Campionatura',
      requiresConfirmation: false,
      color: 'blue',
      icon: <IconPlayerPlay size={16} />,
      allowedRoles: INDUSTRIALIZZAZIONE_ROLES
    },
    {
      from: RequestStatus.PreCampionaturaCompleted,
      to: RequestStatus.PreCampionatura,
      label: 'Riapri Pre-Campionatura',
      description: 'Riapre la fase di Pre-Campionatura per ulteriori modifiche',
      requiresConfirmation: true,
      requiresNote: true,
      color: 'yellow',
      icon: <IconRefresh size={16} />
    },
    {
      from: RequestStatus.PreCampionaturaCompleted,
      to: RequestStatus.Cancelled,
      label: 'Annulla',
      description: 'Annulla definitivamente la richiesta',
      requiresConfirmation: true,
      requiresNote: true,
      color: 'red',
      icon: <IconX size={16} />
    }
  ];

  const campionaturaStatusWorkflow: StatusTransition[] = [...preCampionaturaStatusWorkflow, ...statusWorkflow];

  // Ottieni transizioni disponibili per lo stato corrente
  const getAvailableTransitions = (): StatusTransition[] => {
    // Use Bozzetti workflow for Bozzetti requests, Campionatura workflow for Campionatura
    const workflow = isBozzettiRequest
      ? bozzettiStatusWorkflow
      : isCampionaturaRequest
        ? campionaturaStatusWorkflow
        : statusWorkflow;
    return workflow.filter(transition => {
      if (transition.from !== request.status) return false;
      // Gamma: lo stato Sospeso è definitivo, nessuna transizione in uscita (niente ripresa/annullamento)
      if (isGammaRequest && transition.from === RequestStatus.Suspended) return false;
      // Pre-Campionatura: alcune transizioni sono riservate a un team specifico. Nascondiamo
      // l'azione se l'utente non ha il ruolo richiesto (il backend applica comunque il controllo).
      if (transition.allowedRoles && !hasRole(transition.allowedRoles)) return false;
      return !transition.isAutomatic || canAutoTransition(transition);
    });
  };

  // Verifica se una transizione automatica è possibile
  const canAutoTransition = (transition: StatusTransition): boolean => {
    switch (transition.to) {
      case RequestStatus.InProgress:
        // Può essere avviata automaticamente quando ci sono task
        return (request.tasks?.length || 0) > 0;
      default:
        return false;
    }
  };

  // Gestione transizione di stato
  const handleStatusTransition = (transition: StatusTransition) => {
    if (readOnly) return;

    // For Bozzetti requests, use direct status update (no cross-portal)
    if (isBozzettiRequest) {
      if (transition.requiresConfirmation || transition.requiresNote) {
        setSelectedTransition(transition);
        setTransitionModalOpen(true);
      } else {
        executeBozzettiTransition(transition);
      }
      return;
    }

    // If it's completion for non-Bozzetti, use cross-portal modal
    if (transition.to === RequestStatus.Completed) {
      setCrossPortalModalOpen(true);
      return;
    }

    if (transition.requiresConfirmation || transition.requiresNote) {
      setSelectedTransition(transition);
      setTransitionModalOpen(true);
    } else {
      executeTransition(transition);
    }
  };

  // Esecuzione transizione per richieste Bozzetti (usa API status diretta)
  const executeBozzettiTransition = async (transition: StatusTransition, note?: string) => {
    try {
      await updateStatusMutation.mutateAsync({
        id: request.id,
        status: transition.to,
        notes: note || undefined,
        ...(transitionNeedsClosureDate(transition) && transitionForm.values.closedDate
          ? { completedAt: toDateOnlyISOString(transitionForm.values.closedDate) }
          : {})
      });

      onStatusChange?.(transition.to);

      notifications.show({
        title: 'Stato aggiornato',
        message: `Richiesta ${transition.label.toLowerCase()} con successo`,
        color: 'green',
        icon: <IconCheck size={16} />
      });

      setTransitionModalOpen(false);
      setSelectedTransition(null);
      transitionForm.reset();

    } catch (error) {
      notifications.show({
        title: 'Errore',
        message: 'Errore durante l\'aggiornamento dello stato',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    }
  };

  // Esecuzione transizione - usa updateStatusMutation per cambiare lo status
  const executeTransition = async (transition: StatusTransition, note?: string) => {
    try {
      // Use the dedicated status update endpoint
      await updateStatusMutation.mutateAsync({
        id: request.id,
        status: transition.to,
        notes: note || undefined,
        ...(transitionNeedsClosureDate(transition) && transitionForm.values.closedDate
          ? { completedAt: toDateOnlyISOString(transitionForm.values.closedDate) }
          : {})
      });

      onStatusChange?.(transition.to);

      notifications.show({
        title: 'Stato aggiornato',
        message: `Richiesta ${transition.label.toLowerCase()} con successo`,
        color: 'green',
        icon: <IconCheck size={16} />
      });

      setTransitionModalOpen(false);
      setSelectedTransition(null);
      transitionForm.reset();

    } catch (error) {
      notifications.show({
        title: 'Errore',
        message: 'Errore durante l\'aggiornamento dello stato',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    }
  };

  // Sincronizzazione stato automatica
  const handleSyncStatus = async () => {
    if (readOnly) return;

    setSyncingStatus(true);
    try {
      // Placeholder per sincronizzazione automatica basata sui task
      await syncRequestStatusMutation.mutateAsync(request.id);
      
      notifications.show({
        title: 'Stato sincronizzato',
        message: 'Lo stato è stato sincronizzato automaticamente',
        color: 'blue',
        icon: <IconRefresh size={16} />
      });
    } catch (error) {
      notifications.show({
        title: 'Errore sincronizzazione',
        message: 'Errore durante la sincronizzazione dello stato',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    } finally {
      setSyncingStatus(false);
    }
  };

  // Calcola progress basato sui task
  const calculateProgress = (): number => {
    if (!request.tasks || request.tasks.length === 0) return 0;
    
    const completedTasks = request.tasks.filter(task => task.status === TaskStatus.Done).length;
    return Math.round((completedTasks / request.tasks.length) * 100);
  };

  // Utility functions
  const getStatusInfo = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.New:
        return {
          label: 'Nuova',
          color: 'blue',
          icon: <IconClock size={16} />,
          description: 'La richiesta è in attesa di essere avviata'
        };
      case RequestStatus.PreCampionatura:
        return {
          label: 'Pre-Campionatura',
          color: 'grape',
          icon: <IconClock size={16} />,
          description: 'La richiesta è in fase di Pre-Campionatura (Ufficio Stile)'
        };
      case RequestStatus.PreCampionaturaCompleted:
        return {
          label: 'Pre-Campionatura Completata',
          color: 'indigo',
          icon: <IconCircleCheck size={16} />,
          description: 'Pre-Campionatura completata: in attesa che Industrializzazione avvii la lavorazione'
        };
      case RequestStatus.InProgress:
        return {
          label: 'In Lavorazione',
          color: 'orange',
          icon: <IconPlayerPlay size={16} />,
          description: 'La richiesta è attualmente in lavorazione'
        };
      case RequestStatus.Completed:
        return {
          label: 'Completata',
          color: 'green',
          icon: <IconSquareCheck size={16} />,
          description: 'La richiesta è stata completata con successo'
        };
      case RequestStatus.Cancelled:
        return {
          label: 'Annullata',
          color: 'red',
          icon: <IconX size={16} />,
          description: 'La richiesta è stata annullata'
        };
      case RequestStatus.Suspended:
        return {
          label: 'Sospesa',
          color: 'gray',
          icon: <IconPlayerPause size={16} />,
          description: 'La richiesta è sospesa e può essere ripresa in lavorazione'
        };
      default:
        return {
          label: 'Sconosciuto',
          color: 'gray',
          icon: <IconInfoCircle size={16} />,
          description: 'Stato sconosciuto'
        };
    }
  };

  const getStepperStatus = (status: RequestStatus) => {
    // Simplified stepper for Bozzetti (only 2 steps)
    if (isBozzettiRequest) {
      const bozzettiSteps = [RequestStatus.New, RequestStatus.Completed];
      if (status === RequestStatus.Cancelled || status === RequestStatus.Suspended) {
        return 0; // Show as stopped at first step
      }
      return bozzettiSteps.indexOf(status);
    }

    // Campionatura con Pre-Campionatura: 4 step (le legacy senza questa fase usano i 3 step standard)
    if (hasPreCampionaturaPhase) {
      const campionaturaSteps = [
        RequestStatus.PreCampionatura,
        RequestStatus.PreCampionaturaCompleted,
        RequestStatus.InProgress,
        RequestStatus.Completed
      ];
      if (status === RequestStatus.Cancelled) {
        return 0;
      }
      if (status === RequestStatus.Suspended) {
        return campionaturaSteps.indexOf(RequestStatus.InProgress); // sospesa solo dopo l'avvio
      }
      return Math.max(0, campionaturaSteps.indexOf(status));
    }

    const steps = [
      RequestStatus.New,
      RequestStatus.InProgress,
      RequestStatus.Completed
    ];

    if (status === RequestStatus.Cancelled || status === RequestStatus.Suspended) {
      return steps.indexOf(RequestStatus.InProgress); // Show as stopped in progress
    }

    return steps.indexOf(status);
  };

  const statusInfo = getStatusInfo(request.status);
  const availableTransitions = getAvailableTransitions();
  const progress = calculateProgress();

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={500} size="lg">Stato Richiesta</Text>
        {!readOnly && (
          <Group gap="xs">
            <Tooltip label="Sincronizza stato automaticamente">
              <ActionIcon
                variant="light"
                color="blue"
                onClick={handleSyncStatus}
                loading={syncingStatus}
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        )}
      </Group>

      {/* Status Card */}
      <Card withBorder padding="md">
        <Stack gap="md">
          {/* Current Status */}
          <Group justify="space-between">
            <Group>
              <Badge
                size="lg"
                color={statusInfo.color}
                leftSection={statusInfo.icon}
                variant="light"
              >
                {statusInfo.label}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              {statusInfo.description}
            </Text>
          </Group>

          {/* Progress Bar (solo se in lavorazione) */}
          {request.status === RequestStatus.InProgress && request.tasks && request.tasks.length > 0 && (
            <Box>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>Progresso Task</Text>
                <Text size="sm" c="dimmed">{progress}%</Text>
              </Group>
              <Progress value={progress} color={progress === 100 ? 'green' : 'blue'} />
              <Text size="xs" c="dimmed" mt="xs">
                {request.tasks.filter(t => t.status === TaskStatus.Done).length} di {request.tasks.length} task completati
              </Text>
            </Box>
          )}

          {/* Workflow Stepper - Simplified for Bozzetti, esteso per Campionatura con Pre-Campionatura */}
          {isBozzettiRequest ? (
            <Stepper
              active={getStepperStatus(request.status)}
              color={request.status === RequestStatus.Cancelled ? 'red' : request.status === RequestStatus.Suspended ? 'gray' : 'green'}
              size="sm"
              orientation="horizontal"
            >
              <Stepper.Step
                label="In Attesa"
                description="Richiesta creata"
                icon={<IconClock size={16} />}
              />
              <Stepper.Step
                label="Completata"
                description="Bozzetto completato"
                icon={<IconCircleCheck size={16} />}
              />
            </Stepper>
          ) : hasPreCampionaturaPhase ? (
            <Stepper
              active={getStepperStatus(request.status)}
              color={request.status === RequestStatus.Cancelled ? 'red' : request.status === RequestStatus.Suspended ? 'gray' : 'blue'}
              size="sm"
              orientation="horizontal"
            >
              <Stepper.Step
                label="Pre-Campionatura"
                description="Ufficio Stile"
                icon={<IconClock size={16} />}
              />
              <Stepper.Step
                label="Pronta per Campione"
                description="In attesa di Industrializzazione"
                icon={<IconCircleCheck size={16} />}
              />
              <Stepper.Step
                label="In Lavorazione"
                description="Industrializzazione"
                icon={<IconPlayerPlay size={16} />}
              />
              <Stepper.Step
                label="Completata"
                description="Lavoro terminato"
                icon={<IconSquareCheck size={16} />}
              />
            </Stepper>
          ) : (
            <Stepper
              active={getStepperStatus(request.status)}
              color={request.status === RequestStatus.Cancelled ? 'red' : request.status === RequestStatus.Suspended ? 'gray' : 'blue'}
              size="sm"
              orientation="horizontal"
            >
              <Stepper.Step
                label="In Attesa"
                description="Richiesta creata"
                icon={<IconClock size={16} />}
              />
              <Stepper.Step
                label="In Lavorazione"
                description="Lavoro in corso"
                icon={<IconPlayerPlay size={16} />}
              />
              <Stepper.Step
                label="Completata"
                description="Lavoro terminato"
                icon={<IconSquareCheck size={16} />}
              />
            </Stepper>
          )}

          {/* Available Actions */}
          {!readOnly && availableTransitions.length > 0 && (
            <Box>
              <Text size="sm" fw={500} mb="sm">Azioni Disponibili</Text>
              <Group gap="sm">
                {availableTransitions.slice(0, 2).map((transition, index) => (
                  <Button
                    key={index}
                    leftSection={transition.icon}
                    color={transition.color}
                    variant={transition.color === 'red' ? 'outline' : 'light'}
                    onClick={() => handleStatusTransition(transition)}
                    loading={updateStatusMutation.isPending}
                  >
                    {transition.label}
                  </Button>
                ))}
                
                {availableTransitions.length > 2 && (
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <ActionIcon variant="light">
                        <IconDots size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {availableTransitions.slice(2).map((transition, index) => (
                        <Menu.Item
                          key={index}
                          leftSection={transition.icon}
                          color={transition.color}
                          onClick={() => handleStatusTransition(transition)}
                        >
                          {transition.label}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Group>
            </Box>
          )}

          {/* Auto-sync notification - only for non-Bozzetti requests */}
          {!readOnly && !isBozzettiRequest && request.status === RequestStatus.New && (request.tasks?.length || 0) > 0 && (
            <Alert color="blue" variant="light">
              <Group justify="space-between">
                <Text size="sm">
                  La richiesta può essere avviata automaticamente dato che ci sono task associati
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => handleStatusTransition(
                    statusWorkflow.find(t => t.from === RequestStatus.New && t.to === RequestStatus.InProgress)!
                  )}
                >
                  Avvia Ora
                </Button>
              </Group>
            </Alert>
          )}
        </Stack>
      </Card>

      {/* Modal Conferma Transizione */}
      <Modal
        opened={transitionModalOpen}
        onClose={() => {
          setTransitionModalOpen(false);
          setSelectedTransition(null);
          transitionForm.reset();
        }}
        title={`Conferma: ${selectedTransition?.label}`}
        size="md"
      >
        {selectedTransition && (
          <form onSubmit={transitionForm.onSubmit((values) =>
            isBozzettiRequest
              ? executeBozzettiTransition(selectedTransition, values.note)
              : executeTransition(selectedTransition, values.note)
          )}>
            <Stack gap="md">
              <Alert color={selectedTransition.color} variant="light">
                <Text size="sm">
                  {isGammaRequest && selectedTransition.to === RequestStatus.Suspended
                    ? 'Chiude definitivamente la richiesta Gamma: lo stato resterà "Sospeso" e non potrà essere ripresa.'
                    : selectedTransition.description}
                </Text>
              </Alert>

              {(selectedTransition.requiresNote || selectedTransition.optionalNote) && (
                <Textarea
                  label="Nota"
                  placeholder="Inserisci una nota per questa transizione..."
                  required={!!selectedTransition.requiresNote}
                  minRows={3}
                  {...transitionForm.getInputProps('note')}
                />
              )}

              {transitionNeedsClosureDate(selectedTransition) && (
                <DatePickerInput
                  label={selectedTransition.to === RequestStatus.PreCampionaturaCompleted
                    ? 'Data fine Pre-Campionatura'
                    : 'Data di Chiusura'}
                  placeholder="Seleziona data"
                  leftSection={<IconCalendar size={16} />}
                  valueFormat="DD/MM/YYYY"
                  locale="it"
                  minDate={new Date(request.createdAt)}
                  required
                  {...transitionForm.getInputProps('closedDate')}
                />
              )}

              <Group justify="flex-end">
                <Button
                  variant="light"
                  onClick={() => {
                    setTransitionModalOpen(false);
                    setSelectedTransition(null);
                    transitionForm.reset();
                  }}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  color={selectedTransition.color}
                  loading={updateStatusMutation.isPending}
                >
                  {selectedTransition.label}
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Modal>

      {/* Cross-Portal Completion Modal */}
      <CrossPortalCompletion
        request={request}
        opened={crossPortalModalOpen}
        onClose={() => setCrossPortalModalOpen(false)}
        onCompleted={() => onStatusChange?.(RequestStatus.Completed)}
        userId="current-user-id" // TODO: Get from auth context
        userName="Utente Ufficio Stile" // TODO: Get from auth context
      />
    </Stack>
  );
}
